import React from 'react';
import { BarChart, Trash2, RefreshCw } from 'lucide-react';
import { formatNumber } from '../../../../../utils/formatting';
import Button from '../../../../../components/ui/Button';

/**
 * Компонент боковой панели для сравнения сохраненных расчетов теплопотерь
 */
const ComparisonSidebar = ({ 
  savedCalculations, 
  deleteSavedCalculation, 
  loadSavedCalculation 
}) => {
  // Если нет сохраненных расчетов
  if (!savedCalculations || savedCalculations.length === 0) {
    return (
      <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-5 shadow-sm">
        <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4 flex items-center">
          <BarChart className="w-5 h-5 mr-2 text-red-500" />
          Сравнение расчетов
        </h3>
        <p className="text-sm text-gray-600 dark:text-gray-400">
          Здесь будут отображаться сохраненные варианты расчетов для сравнения различных конфигураций здания.
        </p>
      </div>
    );
  }

  return (
    <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-5 shadow-sm">
      <div className="flex justify-between items-center mb-4">
        <h3 className="text-lg font-medium text-gray-900 dark:text-white flex items-center">
          <BarChart className="w-5 h-5 mr-2 text-red-500" />
          Сравнение расчетов
        </h3>
        <span className="text-sm text-gray-500 dark:text-gray-400">
          {savedCalculations.length} {pluralize(savedCalculations.length, 'вариант', 'варианта', 'вариантов')}
        </span>
      </div>

      <div className="space-y-4">
        {savedCalculations.map((calculation) => (
          <div 
            key={calculation.id} 
            className="bg-gray-50 dark:bg-gray-700/30 border border-gray-200 dark:border-gray-700 rounded-lg p-4 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors group"
          >
            <div className="flex justify-between items-start mb-2">
              <h4 className="text-sm font-medium text-gray-900 dark:text-white">
                {calculation.name}
              </h4>
              <div className="flex items-center space-x-2">
                <Button
                  size="sm"
                  variant="ghost"
                  icon={<RefreshCw className="w-4 h-4" />}
                  onClick={() => loadSavedCalculation(calculation.id)}
                  title="Загрузить параметры"
                />
                <Button
                  size="sm"
                  variant="ghost"
                  icon={<Trash2 className="w-4 h-4 text-red-500" />}
                  onClick={() => deleteSavedCalculation(calculation.id)}
                  title="Удалить"
                />
              </div>
            </div>
            <div className="text-sm text-gray-600 dark:text-gray-400">
              <div className="flex justify-between mb-1">
                <span>Тип здания:</span>
                <span className="font-medium">
                  {calculation.buildingType === 'residential' ? 'Частный дом' : 
                   calculation.buildingType === 'apartment' ? 'Квартира' : 
                   'Коммерческое'}
                </span>
              </div>
              <div className="flex justify-between mb-1">
                <span>Площадь:</span>
                <span>
                  {calculation.formData.floorArea} м²
                </span>
              </div>
              <div className="flex justify-between mb-1">
                <span>Регион:</span>
                <span>
                  {calculation.formData.cityName || calculation.formData.region}
                </span>
              </div>
              <div className="flex justify-between">
                <span>Теплопотери:</span>
                <span className="font-semibold text-red-600 dark:text-red-400">
                  {calculation.results && formatNumber(calculation.results.totalHeatLoss, 0)} Вт
                </span>
              </div>
              <div className="flex justify-between mt-1">
                <span>Удельные потери:</span>
                <span className="font-medium">
                  {calculation.results && formatNumber(calculation.results.specificHeatLoss, 1)} Вт/м²
                </span>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

// Функция для склонения слов в зависимости от числа
const pluralize = (number, one, two, five) => {
  let n = Math.abs(number);
  n %= 100;
  if (n >= 5 && n <= 20) {
    return five;
  }
  n %= 10;
  if (n === 1) {
    return one;
  }
  if (n >= 2 && n <= 4) {
    return two;
  }
  return five;
};

export default ComparisonSidebar;